import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { popoverRoot, useAnchoredPopover } from '../../hooks/useAnchoredPopover';

/**
 * TimePicker — a 24-hour time field in the platform's own look (spec §3.10; a port of
 * `time-picker.tsx`).
 *
 * Owner rule (2026-10-17): times are 24-hour everywhere, never AM/PM. A native
 * <input type="time"> follows the browser's locale (12-hour on an English system) and
 * draws the OS control, so time fields use this instead: the same field and floating
 * panel as DatePicker, hours 00-23 as a 6x4 grid, then minutes in steps (5 by default).
 * Picking the hour keeps the panel open (and moves on to the minutes); picking the minute
 * closes it. The value is "HH:MM", like the native input.
 *
 *   <TimePicker value="14:30" onChange={(hhmm) => setTime(hhmm)} minuteStep={5} allowClear />
 *
 * Props: value, onChange, placeholder, className, minuteStep, allowClear, id, aria-label,
 * disabled, invalid, size ('md' | 'sm'). Keyboard: arrows move inside a grid, Tab goes grid to grid, Esc closes.
 */

const pad = (n) => String(n).padStart(2, '0');
const COLS = 6;

function parse(value) {
  const match = /^(\d{1,2}):(\d{2})/.exec(value || '');
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  return h >= 0 && h < 24 && m >= 0 && m < 60 ? { h, m } : null;
}

function TimePicker({
  value = '',
  onChange,
  placeholder,
  className = '',
  minuteStep = 5,
  allowClear = false,
  id,
  'aria-label': ariaLabel,
  disabled = false,
  invalid = false,
  size = 'md',
}) {
  const { t } = useI18n();
  const uid = useId();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const focusRef = useRef(null); // which grid to focus once the panel is placed: 'h' | 'm'
  const pos = useAnchoredPopover(open, setOpen, anchorRef, popRef);

  const current = parse(value);
  const display = current ? `${pad(current.h)}:${pad(current.m)}` : '';
  const label = placeholder ?? t('ui.timePickerPlaceholder');

  const step = Math.max(1, Math.min(60, Math.round(minuteStep) || 5));
  const minutes = Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  // A stored time off the step (09:13) stays listed and selected.
  if (current && !minutes.includes(current.m)) {
    minutes.push(current.m);
    minutes.sort((a, b) => a - b);
  }

  const now = () => {
    const d = new Date();
    return { h: d.getHours(), m: d.getMinutes() };
  };

  const focusTrigger = () => {
    if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  const openPanel = () => {
    focusRef.current = 'h';
    setOpen(true);
  };

  const pickHour = (h) => {
    onChange?.(`${pad(h)}:${pad(current ? current.m : 0)}`);
    focusRef.current = 'm'; // on to the minutes
  };
  const pickMinute = (m) => {
    onChange?.(`${pad(current ? current.h : now().h)}:${pad(m)}`);
    setOpen(false);
    focusTrigger();
  };
  const pickNow = () => {
    const n = now();
    onChange?.(`${pad(n.h)}:${pad(n.m)}`);
    setOpen(false);
    focusTrigger();
  };
  const clear = () => {
    onChange?.('');
    setOpen(false);
    focusTrigger();
  };

  // Put keyboard focus on the selected cell of the grid we are in: hours on open, minutes after an hour.
  const positioned = Boolean(pos);
  useLayoutEffect(() => {
    if (!open || !positioned || !focusRef.current || !popRef.current) return;
    const grid = popRef.current.querySelector(`[data-grid="${focusRef.current}"]`);
    focusRef.current = null;
    if (!grid) return;
    const cell = grid.querySelector('[tabindex="0"]') || grid.querySelector('button');
    if (cell) cell.focus({ preventScroll: true });
  }, [open, positioned, value]);

  const moveInGrid = (e, grid, index, count) => {
    let next = index;
    switch (e.key) {
      case 'ArrowLeft': next = Math.max(0, index - 1); break;
      case 'ArrowRight': next = Math.min(count - 1, index + 1); break;
      case 'ArrowUp': next = index - COLS >= 0 ? index - COLS : index; break;
      case 'ArrowDown': next = index + COLS < count ? index + COLS : index; break;
      case 'Home': next = 0; break;
      case 'End': next = count - 1; break;
      default: return;
    }
    e.preventDefault();
    const cells = popRef.current?.querySelectorAll(`[data-grid="${grid}"] button`);
    if (cells && cells[next]) cells[next].focus({ preventScroll: true });
  };

  const onPanelKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const items = Array.from(popRef.current?.querySelectorAll('button') || []).filter((b) => !b.disabled && b.tabIndex >= 0);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Roving tab stop: the selected cell, else the first one
  const hourStop = current ? current.h : 0;
  const minuteStop = current ? current.m : minutes[0];

  const panel = open ? (
    <div
      ref={popRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || label}
      className="ui-picker-panel"
      style={pos ? { top: pos.top, left: pos.left, zIndex: pos.zIndex } : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
      onKeyDown={onPanelKeyDown}
    >
      {/* The time as it stands */}
      <p className="ui-time__readout" aria-live="polite">
        {current ? pad(current.h) : '--'}
        <span>:</span>
        {current ? pad(current.m) : '--'}
      </p>

      <p className="ui-time__label" id={`${uid}-hours`}>{t('ui.timePickerHours')}</p>
      <div className="ui-time__grid" role="group" aria-labelledby={`${uid}-hours`} data-grid="h">
        {Array.from({ length: 24 }, (_, h) => (
          <button
            key={h}
            type="button"
            className={`ui-time__cell${current && current.h === h ? ' is-selected' : ''}`}
            tabIndex={h === hourStop ? 0 : -1}
            aria-pressed={Boolean(current && current.h === h)}
            onClick={() => pickHour(h)}
            onKeyDown={(e) => moveInGrid(e, 'h', h, 24)}
          >
            {pad(h)}
          </button>
        ))}
      </div>

      <p className="ui-time__label" id={`${uid}-minutes`}>{t('ui.timePickerMinutes')}</p>
      <div className="ui-time__grid" role="group" aria-labelledby={`${uid}-minutes`} data-grid="m">
        {minutes.map((m, i) => (
          <button
            key={m}
            type="button"
            className={`ui-time__cell${current && current.m === m ? ' is-selected' : ''}`}
            tabIndex={m === minuteStop ? 0 : -1}
            aria-pressed={Boolean(current && current.m === m)}
            onClick={() => pickMinute(m)}
            onKeyDown={(e) => moveInGrid(e, 'm', i, minutes.length)}
          >
            {pad(m)}
          </button>
        ))}
      </div>

      <div className="ui-picker__foot">
        {allowClear ? (
          <button type="button" className="ui-picker__link" onClick={clear}>
            {t('ui.datePickerClear')}
          </button>
        ) : <span />}
        <button type="button" className="ui-picker__link ui-picker__link--accent" onClick={pickNow}>
          {t('ui.timePickerNow')}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div ref={anchorRef} className={`ui-picker${className ? ` ${className}` : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={`ui-select${size === 'sm' ? ' ui-select--sm' : ''}${display ? '' : ' is-placeholder'}`}
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel}${display ? `: ${display}` : ''}` : undefined}
        data-invalid={invalid || undefined}
        disabled={disabled}
      >
        <span className="ui-select__label">{display || label}</span>
        <Clock className="ui-select__icon" aria-hidden="true" />
      </button>
      {panel && createPortal(panel, popoverRoot(anchorRef.current))}
    </div>
  );
}

export default TimePicker;
